"use client";
import React, { useState, useMemo, useEffect } from "react";
import {
  Calendar, X, Plus, Search, Edit2, Trash2, Eye,
  FileText, Gift, Zap, ChevronDown, CheckCircle,
  Download, RefreshCw, Users, DollarSign, Award,
  ChevronsLeft, ChevronsRight
} from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { usePayroll } from "../../context/PayrollContext";
import { useEmployee } from "../../context/EmployeeContext";
import { BASE_URL } from "../../config/apiConfig";
import { useAuth } from "../../context/AuthContext";
import generatePaySlipPDF from "../Employeeportal/Payrolls/Generatepayslippdf";
import { useBonuses } from "../../context/BonusContext";
import { useBonusTypes } from "../../context/BonusTypeContext";

// ─── Shared helpers ────────────────────────────────────────────────────────
const statusBadge = (status) => {
  const base = "px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap";
  if (status === "Paid" || status === "approved") return `${base} bg-green-100 text-green-700`;
  if (status === "Generated" || status === "pending") return `${base} bg-yellow-100 text-yellow-700`;
  return `${base} bg-red-100 text-red-600`;
};

const gradientBtn =
  "text-white px-4 py-2 rounded-lg text-sm font-medium transition-opacity hover:opacity-90";
const gradientStyle = { background: "linear-gradient(135deg, #2563eb, #d12465)" };

// Shared focus classes for lavender-themed inputs/selects/textareas
const focusRing = "focus:outline-none focus:ring-2 focus:ring-[#2563eb]/40 focus:border-[#2563eb] transition";

// ─── Reusable pagination bar (same pattern used across the app) ────────────
function PaginationBar({ currentPage, setCurrentPage, rowsPerPage, setRowsPerPage, totalPages }) {
  return (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-sm bg-white mt-4 pt-4 border-t border-gray-100">
      <div className="text-gray-500">
        Rows per page:
        <select
          className={`border rounded-md px-2 py-1 ml-1 ${focusRing}`}
          value={rowsPerPage}
          onChange={(e) => setRowsPerPage(Number(e.target.value))}
        >
          {[5, 10, 15].map((num) => (
            <option key={num} value={num}>
              {num}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center flex-wrap justify-center gap-1">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronsLeft size={20} />
        </button>
        <button
          className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          <MdKeyboardArrowLeft size={20} />
        </button>

        {(() => {
          let startPage = Math.max(currentPage - 1, 1);
          let endPage = startPage + 2;
          if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(endPage - 2, 1);
          }
          const pages = [];
          for (let i = startPage; i <= endPage; i++) {
            pages.push(
              <button
                key={i}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  i === currentPage
                    ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                onClick={() => setCurrentPage(i)}
              >
                {i}
              </button>
            );
          }
          return pages;
        })()}

        <button
          className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          <MdKeyboardArrowRight size={20} />
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronsRight size={20} />
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 1 — BONUS MANAGEMENT (demo data — not yet connected to a backend)
// ════════════════════════════════════════════════════════════════════════════
function BonusManagement() {
  const {bonuses, loading, error, setBonuses, fetchBonuses, createBonus, updateBonus, deleteBonus } = useBonuses();
  const { employees } = useEmployee();
  const { bonusTypes, fetchBonusTypes } = useBonusTypes();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selected, setSelected] = useState(null);

  // ── Pagination (same pattern as Payslip Generation) ──
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    fetchBonuses();
    fetchBonusTypes();
  }, []);

  const bonusList = Array.isArray(bonuses) ? bonuses : bonuses ? [bonuses] : [];
  const statusOptions = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
];

  const [form, setForm] = useState({
  empId: "",
  type: "",
  amount: "",
  month: "",
  note: "",
  status: "pending",
});

  const resetAddForm = () => {
    setForm({
      empId: "",
      type: "",
      amount: "",
      month: "",
      note: "",
      status: "pending",
    });
  };

  // Close (X) and Cancel should behave identically — both discard the draft.
  const handleCloseAddModal = () => {
    setModal(false);
    resetAddForm();
  };

  const activeBonusTypes = useMemo(() => {
  return Array.isArray(bonusTypes)
    ? bonusTypes.filter(bt => bt.status === "active")
    : [];
}, [bonusTypes]);

  const filtered = useMemo(() => {
  return bonusList.filter((b) => {
    const matchStatus =
      filter === "All" ||
      b.status.toLowerCase() === filter.toLowerCase();

    const matchSearch =
      (b.empName || "")
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      (b.bonus_type_name || "")
        .toLowerCase()
        .includes(search.toLowerCase());

    return matchStatus && matchSearch;
  });
}, [bonusList, search, filter]);

  // ── Pagination derived values ──
  const totalPages = Math.max(Math.ceil(filtered.length / rowsPerPage), 1);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filtered.slice(indexOfFirstRow, indexOfLastRow);

  // Reset to page 1 whenever the filters or page size change so the user
  // never lands on an empty page after narrowing the results.
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filter, rowsPerPage]);

const getUserId = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  return user?.id || user?.user_id;
};

  const handleAdd = async (e) => {
  e.preventDefault();

  // convert "2026-12" → "2026-12-01" (or any fixed day like 01 or 05)
  const formattedDate = form.month
    ? `${form.month}-05`
    : null;

  const payload = {
    user_id: form.empId,
    bonus_type_id: form.type,
    amount: form.amount,
    date: formattedDate, // ✅ YYYY-MM-DD format
    note: form.note,
    status: form.status,

    create_audit_id: getUserId(),
  };

  const res = await createBonus(payload);

  if (res.success) {
    setModal(false);
    resetAddForm();

    fetchBonuses();
  } else {
    alert(res.message);
  }
};

  const handleEditSubmit = async (e) => {
  e.preventDefault();

  const payload = {
    user_id: form.empId,
    bonus_type_id: Number(form.type),
    amount: form.amount,
    date: form.month?.length === 7 ? `${form.month}-05` : form.month,
    note: form.note,
    status: form.status,
    update_audit_id: getUserId(),
  };

  const res = await updateBonus(selected.id, payload);

  if (res.success) {
    setEditModal(false);
    setSelected(null);
    resetAddForm();
    fetchBonuses();
  } else {
    alert(res.message);
  }
};

const openEdit = (b) => {
  setSelected(b);

  setForm({
    empId: b.empId || b.user_id,
    type: b.bonus_type_id,   // IMPORTANT (must be id)
    amount: b.amount,
    month: b.date, // YYYY-MM-DD
    note: b.note,
    status: b.status,
  });

  setEditModal(true);
};

const handleCloseEditModal = () => {
  setEditModal(false);
  setSelected(null);
  resetAddForm();
};

  // const totalBudget = bonuses.filter(b => b.status === "Approved").reduce((s, b) => s + b.amount, 0);
  // const pendingCount = bonuses.filter(b => b.status === "Pending").length;

  const totalBudget = bonusList
  .filter((b) => b.status === "approved")
  .reduce((sum, b) => sum + Number(b.amount), 0);

const pendingCount = bonusList.filter(
  (b) => b.status === "pending"
).length;

const handleDelete = async () => {
  if (!selected) return;

  const res = await deleteBonus(
    selected.id,
    getUserId()
  );

  if (res.success) {
    setDeleteModal(false);
    setSelected(null);
    fetchBonuses(); // refresh list
  } else {
    alert(res.message);
  }
};

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: "Total Approved Budget", value: `₹${totalBudget.toLocaleString()}`, icon: <DollarSign size={18}/>, color: "text-green-600 bg-green-50" },
          { label: "Pending Approvals",     value: pendingCount,                         icon: <RefreshCw size={18}/>, color: "text-yellow-600 bg-yellow-50" },
          { label: "Total Bonuses",          value: bonusList.length,                       icon: <Award size={18}/>,     color: "text-blue-600 bg-blue-50" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl p-4 flex items-center gap-3 shadow-sm border border-gray-100">
            <div className={`p-2 rounded-lg ${s.color}`}>{s.icon}</div>
            <div>
              <p className="text-xs text-gray-400">{s.label}</p>
              <p className="text-lg font-bold text-gray-800">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl py-3 px-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15}/>
          <input type="text" placeholder="Search bonuses..." value={search} onChange={e => setSearch(e.target.value)}
            className={`w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm ${focusRing}`}/>
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className={`border border-gray-300 rounded-lg px-3 py-2 text-sm w-full sm:w-36 ${focusRing}`}>
          <option>All</option><option>Approved</option><option>Pending</option>
        </select>
        {(search || filter !== "All") && (
          <button
            type="button"
            onClick={() => { setSearch(""); setFilter("All"); }}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
          >
            <X size={14} />
            Clear Filters
          </button>
        )}
        <button onClick={() => { resetAddForm(); setModal(true); }} className={`${gradientBtn} flex items-center gap-1.5 justify-center sm:ml-auto`} style={gradientStyle}>
          <Plus size={16}/> Add Bonus
        </button>
      </div>

      {/* Table card — desktop table, mobile cards, and pagination all share one card */}
      <div className="bg-white shadow-md rounded-2xl overflow-hidden p-4">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead className="bg-sky-50 text-gray-600">
              <tr>{["Employee","Type","Amount","Month","Note","Status","Actions"].map(h => <th key={h} className="p-3 whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? currentRows.map((b, idx) => (
                <tr key={b.id} className={`border-b hover:bg-gray-50 ${idx % 2 !== 0 ? "bg-[#f7f6fe]" : ""}`}>
                  <td className="p-3 text-gray-800">{b.empName}</td>
                  <td className="p-3"><span className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">{b.bonus_type_name}</span></td>
                  <td className="p-3 text-gray-700">₹{Number(b.amount).toLocaleString()}</td>
                  <td className="p-3 text-gray-600">{b.date}</td>
                  <td className="p-3 text-gray-500 max-w-[160px] truncate">{b.note}</td>
                  <td className="p-3"><span className={statusBadge(b.status)}>
                    {b.status.charAt(0).toUpperCase() + b.status.slice(1)}
                  </span></td>
                  <td className="p-3">
                    <div className="flex gap-3">
                      <button className="text-blue-600 hover:text-blue-800" onClick={() => openEdit(b)}><Edit2 size={16}/></button>
                      <button className="text-red-500 hover:text-red-700" onClick={() => { setSelected(b); setDeleteModal(true); }}><Trash2 size={16}/></button>
                    </div>
                  </td>
                </tr>
              )) : <tr><td colSpan={7} className="text-center py-10 text-gray-400">No records found</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3 p-3">
          {currentRows.length > 0 ? currentRows.map(b => (
            <div key={b.id} className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex justify-between mb-2">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{b.empName}</p>
                  <p className="text-xs text-gray-400">{b.bonus_type_name} · {b.date}</p>
                </div>
                <span className={statusBadge(b.status)}>
                  {b.status.charAt(0).toUpperCase() + b.status.slice(1)}
              </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-green-700 font-bold">₹{Number(b.amount).toLocaleString()}</span>
                <div className="flex gap-3">
                  <button className="text-blue-600" onClick={() => openEdit(b)}><Edit2 size={15}/></button>
                  <button className="text-red-500" onClick={() => { setSelected(b); setDeleteModal(true); }}><Trash2 size={15}/></button>
                </div>
              </div>
              {b.note && <p className="text-xs text-gray-400 mt-1">{b.note}</p>}
            </div>
          )) : (
            <div className="text-center py-10 text-gray-400">No records found</div>
          )}
        </div>

        {/* Pagination — inside the same card as the table */}
        <PaginationBar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          totalPages={totalPages}
        />
      </div>

      {/* Add Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">Add Bonus</h3>
              <button onClick={handleCloseAddModal}><X size={20} className="text-gray-500"/></button>
            </div>
            <form className="space-y-3" onSubmit={handleAdd}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
                  <select
                    required
                    value={form.empId}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        empId: e.target.value,
                      }))
                    }
                    className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}
                  >
                    <option value="">Select Employee</option>

                    {employees?.filter(emp => emp.status?.toLowerCase() === "active").map((emp) => (
                      <option key={emp.empId} value={emp.empId}>
                        {emp.first_name} {emp.last_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Bonus Type</label>
                  <select
                    value={form.type}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, type: e.target.value }))
                    }
                    className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}
                  >
                    <option value="">Select Bonus Type</option>

                    {activeBonusTypes.filter(bt => bt.status?.toLowerCase() === "active").map((bt) => (
                      <option key={bt.id} value={bt.id}>
                        {bt.name.charAt(0).toUpperCase() + bt.name.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Amount (₹)</label>
                  <input required type="number" placeholder="e.g. 5000" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
                  <input required type="month" value={form.month} onChange={e => setForm(f => ({...f, month: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
                </div>
                <div>
  <label className="block text-xs font-medium text-gray-600 mb-1">
    Status
  </label>

  <select
    value={form.status}
    onChange={(e) =>
      setForm((f) => ({
        ...f,
        status: e.target.value,
      }))
    }
    className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}
  >
    {statusOptions.map((status) => (
      <option key={status.value} value={status.value}>
        {status.label}
      </option>
    ))}
  </select>
</div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Note</label>
                  <input type="text" placeholder="Reason / note" value={form.note} onChange={e => setForm(f => ({...f, note: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={handleCloseAddModal} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" className={gradientBtn} style={gradientStyle}>Add Bonus</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">Edit Bonus — {selected?.empName}</h3>
              <button onClick={handleCloseEditModal}><X size={20} className="text-gray-500"/></button>
            </div>
            <form className="space-y-3" onSubmit={handleEditSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Bonus Type</label>
                  <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}>
                    {activeBonusTypes.map((bt) => (
                      <option key={bt.id} value={Number(bt.id)}>
                        {bt.name.charAt(0).toUpperCase() + bt.name.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Amount (₹)</label>
                  <input type="number" value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
                  <input type="date" value={form.month} onChange={e => setForm(f => ({...f, month: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Status
                  </label>

                  <select
                    value={form.status}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        status: e.target.value,
                      }))
                    }
                    className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}
                  >
                    {statusOptions.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Note</label>
                  <input type="text" value={form.note} onChange={e => setForm(f => ({...f, note: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={handleCloseEditModal} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" className={gradientBtn} style={gradientStyle}>Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-lg p-6 w-full sm:max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete Bonus</h3>
            <p className="text-sm text-gray-500 mb-5">Remove ₹{selected?.amount?.toLocaleString()} {selected?.type} bonus for <strong>{selected?.empName}</strong>?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteModal(false)} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={handleDelete} className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 2 — PAYROLL DOCUMENTS (demo data — not yet connected to a backend)
// ════════════════════════════════════════════════════════════════════════════
function PayrollDocuments() {
  const [docs, setDocs] = useState([
    { id: 1, name: "Salary Register – May 2025",     type: "Salary Register", size: "142 KB", date: "2025-06-01", status: "Final" },
    { id: 2, name: "PF Challan – May 2025",           type: "PF Document",     size: "85 KB",  date: "2025-06-03", status: "Final" },
    { id: 3, name: "ESI Challan – May 2025",          type: "ESI Document",    size: "74 KB",  date: "2025-06-03", status: "Final" },
    { id: 4, name: "Professional Tax – May 2025",     type: "Tax Document",    size: "56 KB",  date: "2025-06-04", status: "Draft" },
    { id: 5, name: "Salary Register – Apr 2025",     type: "Salary Register", size: "138 KB", date: "2025-05-02", status: "Final" },
    { id: 6, name: "Form 16 – FY 2024-25",            type: "Income Tax",      size: "320 KB", date: "2025-05-20", status: "Final" },
  ]);
  const [search, setSearch] = useState("");
  const [addModal, setAddModal] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [form, setForm] = useState({ name: "", type: "Salary Register", date: "", status: "Draft" });

  // ── Pagination (same pattern as Payslip Generation) ──
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const docTypes = ["Salary Register", "PF Document", "ESI Document", "Tax Document", "Income Tax"];

  const filtered = docs.filter(d => d.name.toLowerCase().includes(search.toLowerCase()) || d.type.toLowerCase().includes(search.toLowerCase()));
  const typeColor = { "Salary Register": "bg-blue-50 text-blue-700", "PF Document": "bg-sky-50 text-sky-700", "ESI Document": "bg-indigo-50 text-indigo-700", "Tax Document": "bg-orange-50 text-orange-700", "Income Tax": "bg-red-50 text-red-700" };

  // ── Pagination derived values ──
  const totalPages = Math.max(Math.ceil(filtered.length / rowsPerPage), 1);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filtered.slice(indexOfFirstRow, indexOfLastRow);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, rowsPerPage]);

  const handleAdd = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.date) return;
    const newDoc = { id: Date.now(), name: form.name, type: form.type, size: "—", date: form.date, status: form.status };
    setDocs(prev => [newDoc, ...prev]);
    setAddModal(false);
    setForm({ name: "", type: "Salary Register", date: "", status: "Draft" });
  };

  // ── Action handlers (previously missing — buttons had no onClick at all) ──
  const handleView = (doc) => setViewDoc(doc);

  const handleDownload = (doc) => {
    // No backend wired up yet for this tab, so we generate a placeholder
    // text file client-side just so the button has a real, visible effect.
    // Replace this with a real API call once documents are backed by the server, e.g.:
    //   const res = await fetch(`${BASE_URL}/payroll-documents/${doc.id}/download`, {
    //     headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    //   });
    //   const blob = await res.blob();
    //   ...then trigger the same anchor-click download pattern below.
    const content = `Document: ${doc.name}\nType: ${doc.type}\nSize: ${doc.size}\nDate: ${doc.date}\nStatus: ${doc.status}`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${doc.name}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl py-3 px-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15}/>
          <input type="text" placeholder="Search documents..." value={search} onChange={e => setSearch(e.target.value)}
            className={`w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm ${focusRing}`}/>
        </div>
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
          >
            <X size={14} />
            Clear Filters
          </button>
        )}
        <button onClick={() => setAddModal(true)} className={`${gradientBtn} flex items-center gap-1.5 justify-center sm:ml-auto`} style={gradientStyle}>
          <Plus size={16}/> Add New
        </button>
      </div>

      <div className="bg-white shadow-md rounded-2xl overflow-hidden p-4">
        <table className="w-full text-sm text-left border-collapse hidden md:table">
          <thead className="bg-sky-50 text-gray-600">
            <tr>{["Document Name","Type","Size","Date","Status","Actions"].map(h => <th key={h} className="p-3 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {currentRows.length > 0 ? currentRows.map((d, idx) => (
              <tr key={d.id} className={`border-b hover:bg-gray-50 ${idx % 2 !== 0 ? "bg-[#f7f6fe]" : ""}`}>
                <td className="p-3 text-gray-800 flex items-center gap-2"><FileText size={15} className="text-gray-400 shrink-0"/>{d.name}</td>
                <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColor[d.type] || "bg-gray-100 text-gray-600"}`}>{d.type}</span></td>
                <td className="p-3 text-gray-500">{d.size}</td>
                <td className="p-3 text-gray-500">{d.date}</td>
                <td className="p-3"><span className={statusBadge(d.status === "Final" ? "Approved" : "Pending")}>{d.status}</span></td>
                <td className="p-3">
                  <div className="flex gap-3">
                    <button onClick={() => handleView(d)} className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-xs">
                      <Eye size={14}/> View
                    </button>
                    <button onClick={() => handleDownload(d)} className="text-green-600 hover:text-green-800 flex items-center gap-1 text-xs">
                      <Download size={14}/> Download
                    </button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={6} className="text-center py-10 text-gray-400">No records found</td></tr>
            )}
          </tbody>
        </table>

        <div className="md:hidden space-y-3 p-3">
          {currentRows.length > 0 ? currentRows.map(d => (
            <div key={d.id} className="border border-gray-100 rounded-xl p-4">
              <div className="flex justify-between mb-1">
                <p className="font-medium text-gray-800 text-sm">{d.name}</p>
                <span className={statusBadge(d.status === "Final" ? "Approved" : "Pending")}>{d.status}</span>
              </div>
              <p className="text-xs text-gray-400 mb-2">{d.type} · {d.size} · {d.date}</p>
              <div className="flex gap-3">
                <button onClick={() => handleView(d)} className="text-blue-600 flex items-center gap-1 text-xs">
                  <Eye size={13}/> View
                </button>
                <button onClick={() => handleDownload(d)} className="text-green-600 flex items-center gap-1 text-xs">
                  <Download size={13}/> Download
                </button>
              </div>
            </div>
          )) : (
            <div className="text-center py-10 text-gray-400">No records found</div>
          )}
        </div>

        {/* Pagination — inside the same card as the table */}
        <PaginationBar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          totalPages={totalPages}
        />
      </div>

      {/* View Document Modal */}
      {viewDoc && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Document Details</h3>
              <button onClick={() => setViewDoc(null)}><X size={20} className="text-gray-500"/></button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 rounded-lg bg-blue-50 text-blue-600"><FileText size={18}/></div>
                <p className="font-semibold text-gray-800">{viewDoc.name}</p>
              </div>
              <p className="flex justify-between border-b border-gray-100 py-1.5"><span className="text-gray-400">Type</span><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColor[viewDoc.type] || "bg-gray-100 text-gray-600"}`}>{viewDoc.type}</span></p>
              <p className="flex justify-between border-b border-gray-100 py-1.5"><span className="text-gray-400">Size</span><span className="font-medium text-gray-800">{viewDoc.size}</span></p>
              <p className="flex justify-between border-b border-gray-100 py-1.5"><span className="text-gray-400">Date</span><span className="font-medium text-gray-800">{viewDoc.date}</span></p>
              <p className="flex justify-between py-1.5"><span className="text-gray-400">Status</span><span className={statusBadge(viewDoc.status === "Final" ? "Approved" : "Pending")}>{viewDoc.status}</span></p>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setViewDoc(null)} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Close</button>
              <button onClick={() => handleDownload(viewDoc)} className={`${gradientBtn} flex items-center gap-2`} style={gradientStyle}>
                <Download size={14}/> Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Document Modal */}
      {addModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">Add Document</h3>
              <button onClick={() => setAddModal(false)}><X size={20} className="text-gray-500"/></button>
            </div>
            <form className="space-y-3" onSubmit={handleAdd}>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Document Name</label>
                <input required type="text" placeholder="e.g. Salary Register – June 2025" value={form.name}
                  onChange={e => setForm(f => ({...f, name: e.target.value}))}
                  className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                  <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}>
                    {docTypes.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                  <input required type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}/>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}>
                  <option>Draft</option><option>Final</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setAddModal(false)} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" className={gradientBtn} style={gradientStyle}>Add Document</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TAB 3 — PAYSLIP GENERATION (REAL — wired to PayrollContext / EmployeeContext)
// ════════════════════════════════════════════════════════════════════════════
function PayslipGeneration() {
  const { payrolls, createPayroll, updatePayroll, deletePayroll, generateMonthlyPayroll, fetchPayrolls } = usePayroll();
  const { canDo } = useAuth();
  const { employees, getEmployeeById, loading: empLoading } = useEmployee();

  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [status, setStatus] = useState("Paid");
  const [searchTerm, setSearchTerm] = useState("");
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [viewModal, setViewModal] = useState(false);

  // ── Month filter for the payslip table (separate from the bulk-generate month) ──
  const [monthFilter, setMonthFilter] = useState("");

  // ── Pagination (same pattern as Leaves) ──
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [editSelectedEmployee, setEditSelectedEmployee] = useState("");
  const [editTotalWorkingDays, setEditTotalWorkingDays] = useState("");
  const [editPayableDays, setEditPayableDays] = useState("");
  const [editSelectedDate, setEditSelectedDate] = useState("");
  const [editStatus, setEditStatus] = useState("Paid");
  const [editHra, setEditHra] = useState("");
  const [editSpecialAllowance, setEditSpecialAllowance] = useState("");
  const [editMed, setEditMed] = useState("");
  const [editPfEmployee, setEditPfEmployee] = useState("");
  const [editTaxesDeductions, setEditTaxesDeductions] = useState("");
  const [editProfessionalTax, setEditProfessionalTax] = useState("");

  const [totalWorkingDays, setTotalWorkingDays] = useState("");
  const [payableDays, setPayableDays] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  const [hra, setHra] = useState("");
  const [specialAllowance, setSpecialAllowance] = useState("");
  const [med, setMed] = useState("");
  const [pfEmployee, setPfEmployee] = useState("");
  const [taxesDeductions, setTaxesDeductions] = useState("");
  const [professionalTax, setProfessionalTax] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");
  const [companyName, setCompanyName] = useState("ALGORITHMICA CONSULTANCY SERVICES PRIVATE LIMITED.");

  // ── Bulk "Generate All" PDF state ──
  const [generateModal, setGenerateModal] = useState(false);
  const [genMonth, setGenMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [generating, setGenerating] = useState(false);
  const [genDone, setGenDone] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [genTotal, setGenTotal] = useState(0);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user?.id || null;

  const fetchAttendanceSummary = async (employeeId, date) => {
    if (!employeeId || !date) return;
    const token = localStorage.getItem("token");
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/attendance/monthly-summary`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: employeeId, date }),
      });
      const data = await res.json();
      if (res.ok) {
        setTotalWorkingDays(data.total_working_days || 0);
        setPayableDays(data.payable_days || 0);
      } else {
        setTotalWorkingDays(0);
        setPayableDays(0);
      }
    } catch (error) {
      console.error("Error fetching attendance summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEmployee = async (id) => {
    setSelectedEmployee(id);
    setSelectedDate("");
    setTotalWorkingDays("");
    setPayableDays("");
    if (!id) { setEmployeeDetails(null); return; }
    try {
      setLoading(true);
      const data = await getEmployeeById(id);
      setEmployeeDetails(data || null);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // ── FIX: this now fully clears every field of the Add New form, including
  // selectedDate (previously reset to `null`, which React treats differently
  // from an empty string on a controlled <input>, and status (previously
  // reset to "" instead of the form's real default "Paid"). This is what's
  // called both on successful submit AND on Cancel / the modal's X button,
  // so re-opening "Add New" always starts from a clean slate. ──
  const resetForm = () => {
    setCompanyLogo("");
    setCompanyName("ALGORITHMICA CONSULTANCY SERVICES PRIVATE LIMITED.");
    setSelectedEmployee("");
    setSelectedDate("");
    setEmployeeDetails(null);
    setHra("");
    setSpecialAllowance("");
    setMed("");
    setPfEmployee("");
    setTaxesDeductions("");
    setProfessionalTax("");
    setStatus("Paid");
    setTotalWorkingDays("");
    setPayableDays("");
  };

  // Close (X) and Cancel should behave identically — both discard the draft.
  const handleCloseAddModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) { alert("User not logged in!"); return; }
    const payload = {
      user_id: selectedEmployee,
      payroll_date: selectedDate,
      total_payable_days: totalWorkingDays,
      days_payable: payableDays,
      status,
      hra,
      special_allowances: specialAllowance,
      med,
      pf_employee: pfEmployee,
      tax: taxesDeductions,
      professional_tax: professionalTax,
      create_audit_id: userId,
    };
    try {
      const res = await createPayroll(payload);
      if (res?.success) {
        setIsModalOpen(false);
        resetForm();
      } else {
        alert(res?.message || "⚠️ Failed to create payslip.");
        resetForm();
      }
    } catch (error) {
      resetForm();
      alert("Something went wrong while creating payslip.");
    }
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setEditSelectedEmployee(row.user_id);
    setEditTotalWorkingDays(row.total_payable_days || "");
    setEditPayableDays(row.days_payable || "");
    setEditSelectedDate(row.payroll_date || "");
    setEditStatus(row.status || "Paid");
    setEditHra(row.hra || "");
    setEditSpecialAllowance(row.special_allowances || "");
    setEditMed(row.med || "");
    setEditPfEmployee(row.pf_employee || "");
    setEditTaxesDeductions(row.tax || "");
    setEditProfessionalTax(row.professional_tax || "");
    setEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      user_id: editSelectedEmployee,
      payroll_date: editSelectedDate,
      total_payable_days: editTotalWorkingDays,
      days_payable: editPayableDays,
      status: editStatus,
      hra: editHra,
      special_allowances: editSpecialAllowance,
      med: editMed,
      pf_employee: editPfEmployee,
      tax: editTaxesDeductions,
      professional_tax: editProfessionalTax,
      update_audit_id: userId,
    };
    try {
      const res = await updatePayroll(selectedRow.id, payload);
      if (res.success) {
        setEditModal(false);
      } else {
        alert(res.message);
      }
    } catch (error) {
      console.error("❌ Error while updating payslip:", error);
    }
  };

  const handleDelete = async (id) => {
    const res = await deletePayroll(id, userId);
    if (res?.success) {
      setDeleteModal(false);
      setSelectedRow(null);
    } else {
      alert(res?.message || "Failed to delete payslip");
    }
  };

  // ── FIX: month filter is now actually applied to the table, not just used
  // by the bulk-generate banner. Selecting a month in the toolbar filters
  // filteredPayroll down to records whose payroll_date falls in that month. ──
  const filteredPayroll = useMemo(() => {
    if (!payrolls) return [];
    return payrolls.filter((p) => {
      const matchesStatus = statusFilter === "All" || p.status?.toLowerCase() === statusFilter.toLowerCase();
      const matchesSearch =
        p.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.user_id?.toString().includes(searchTerm);
      const matchesMonth = !monthFilter || p.payroll_date?.startsWith(monthFilter);
      return matchesStatus && matchesSearch && matchesMonth;
    });
  }, [payrolls, searchTerm, statusFilter, monthFilter]);

  // Records that belong to the month picked in the "Generate All" banner
  const recordsForGenMonth = useMemo(() => {
    if (!payrolls) return [];
    return payrolls.filter((p) => p.payroll_date?.startsWith(genMonth));
  }, [payrolls, genMonth]);

  // ── Pagination derived values ──
  const totalPages = Math.max(Math.ceil(filteredPayroll.length / rowsPerPage), 1);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredPayroll.slice(indexOfFirstRow, indexOfLastRow);

  // Whenever the filters or page size change, jump back to page 1 so the
  // user never lands on an empty page after narrowing the results.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, monthFilter, rowsPerPage]);

  // ── PDF helpers (same calculation used by the per-row Download button) ──
  const buildPdfPayload = (row) => {
    const [year, month] = row.payroll_date.split("-").map(Number);
    const salary = parseFloat(String(row.salary).replace(/[^\d.]/g, "")) || 0;
    const basicDA = Math.round(salary * 0.5);
    const hraAmt = Math.round(salary * 0.2);
    const otherAllowance = salary - basicDA - hraAmt;
    const monthDays = new Date(year, month, 0).getDate();
    const paidDaysVal = row.paid_days ?? 26;
    return {
      companyName,
      companyAddress: "D.No3-51, Kodali vari street, Enikepadu, Vijayawada, Krishna Andhra Pradesh. 521108",
      companyLogoUrl: companyLogo || "https://cdn.acsdev.in/fynryx/acs.png",
      empId: String(row.user_id ?? ""),
      employeeName: `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim(),
      designation: row.deg_name ?? "",
      location: row.location ?? "",
      month: new Date(year, month - 1).toLocaleString("default", { month: "long" }).toUpperCase(),
      year,
      monthDays,
      paidDays: paidDaysVal,
      utrRef: row.acc_no ?? "",
      dateOfJoining: row.joiningdate ?? "",
      earnings: {
        basicDA,
        basicDAEarned: Math.round((basicDA / monthDays) * paidDaysVal),
        hra: hraAmt,
        hraEarned: Math.round((hraAmt / monthDays) * paidDaysVal),
        otherAllowance,
        otherAllowanceEarned: Math.round((otherAllowance / monthDays) * paidDaysVal),
        extraEarnings: 0,
        extraEarningsEarned: 0,
      },
      deductions: {
        epf: Math.round(basicDA * 0.12),
        esi: salary <= 21000 ? Math.round(salary * 0.0075) : 0,
        pt: salary > 15000 ? 200 : salary > 10000 ? 150 : 0,
        salaryAdvance: 0,
        insurance: 0,
      },
      netEarningsInWords: "",
    };
  };

  const downloadPayslipPDF = (row) => {
    if (!row?.payroll_date) {
      alert("This record has no payroll date — can't generate a PDF for it.");
      return;
    }
    generatePaySlipPDF({
  empId: row.user_id,
  employeeName: `${row.first_name} ${row.last_name}`,
  designation: row.deg_name,
  department : row.dep_name,
  location : row.work_location,

  bankName: row.bank,
  bankAccountNo: row.acc_no,
  panNo: row.pan_no,
  uanNo: row.uan_no,

  dateOfJoining: row.joiningdate,
  dateOfBirth : row.dob,

  monthYear: new Date(row.payroll_date).toLocaleString("en-IN", {
    month: "long",
    year: "numeric",
  }),

  totalWorkingDays: row.total_payable_days,
  netPaidDays: row.days_payable,
  lopDays: Number(row.total_payable_days) - Number(row.days_payable),

  earnings: {
    rows: [
      { label: "Basic+DA", monthly: row.basic_salary },
      { label: "HRA", monthly: row.hra },
      // { label: "Conveyance", monthly: row.conveyance_allowance },
      { label: "Other Allowance", monthly: row.special_allowances },
      { label: "Medical", monthly: row.med }
    ]
  },

  deductions: {
    rows: [
      { label: "PF", monthly: row.pf_employee },
      { label: "Professional Tax", monthly: row.professional_tax },
      // { label: "Income Tax", monthly: row.tax }
    ]
  }
});
  };

 const handleGenerateAll = async () => {
  setGenerating(true);
  setGenDone(false);
  setGenProgress(0);

  const [year, month] = genMonth.split("-");

  const res = await generateMonthlyPayroll({
    year,
    month,
  });

  if (!res.success) {
    setGenerating(false);
    return;
  }

  await fetchPayrolls();

  const monthRecords = payrolls.filter((p) =>
    p.payroll_date?.startsWith(genMonth)
  );

  if (monthRecords.length === 0) {
    setGenerating(false);
    return;
  }

  setGenTotal(monthRecords.length);

  for (let i = 0; i < monthRecords.length; i++) {
    downloadPayslipPDF(monthRecords[i]);
    setGenProgress(i + 1);
    await new Promise((resolve) => setTimeout(resolve, 450));
  }

  setGenerating(false);
  setGenDone(true);
};

  return (
    <div className="space-y-4">
      {/* Generate-All Banner */}
      <div className="bg-white rounded-2xl p-5 border-2 border-dashed border-blue-200 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
            <Zap size={18} className="text-blue-600" /> Bulk Generate &amp; Download Payslips
          </h3>
          <p className="text-sm text-gray-500">
            Pick a month and download a PDF payslip for every record already created in that month.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <input
            type="month"
            value={genMonth}
            onChange={(e) => setGenMonth(e.target.value)}
            className={`border border-gray-300 rounded-lg px-3 py-2 text-sm ${focusRing}`}
          />
          {canDo("Payroll", "Add") && (
            <button
              onClick={() => { setGenerateModal(true); setGenDone(false); setGenProgress(0); }}
              className={`${gradientBtn} flex items-center gap-2`}
              style={gradientStyle}
            >
              <Zap size={15} /> Generate All
            </button>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl py-3 px-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={15} />
            <input
              type="text"
              placeholder="Search payslips..."
              className={`w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-700 ${focusRing}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 w-full sm:w-36 shrink-0 ${focusRing}`}
          >
            <option value="All">All</option>
            <option value="Paid">Paid</option>
            <option value="Unpaid">Unpaid</option>
            <option value="generated">Generated</option>
          </select>

          {/* Month filter — actually filters the table below */}
          <div className={`flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 w-full sm:w-auto shrink-0 focus-within:ring-2 focus-within:ring-[#2563eb]/40 focus-within:border-[#2563eb] transition`}>
            <input
              type="month"
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              className="text-sm text-gray-700 focus:outline-none w-full sm:w-auto"
            />
            {monthFilter && (
              <button
                type="button"
                onClick={() => setMonthFilter("")}
                className="text-xs text-gray-400 hover:text-gray-600 shrink-0"
                title="Clear month filter"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {(searchTerm || statusFilter !== "All" || monthFilter) && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("All");
                setMonthFilter("");
              }}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
            >
              <X size={14} />
              Clear Filters
            </button>
          )}

          {canDo("Payroll", "Add") && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center justify-center gap-1.5 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium whitespace-nowrap shrink-0 sm:ml-auto"
              style={gradientStyle}
            >
              <Plus size={16} /> Add New
            </button>
          )}
        </div>
      </div>

      {/* Table card — desktop table, mobile cards, and pagination all share one card */}
      <div className="bg-white shadow-md rounded-2xl overflow-hidden p-4">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm">
            <thead className="bg-sky-50 text-gray-600">
              <tr>
                <th className="p-3 whitespace-nowrap">ID</th>
                <th className="p-3 whitespace-nowrap">Date</th>
                <th className="p-3 whitespace-nowrap">Employee Name</th>
                <th className="p-3 whitespace-nowrap">Bank Name</th>
                <th className="p-3 whitespace-nowrap">Account No</th>
                <th className="p-3 whitespace-nowrap">Designation</th>
                <th className="p-3 whitespace-nowrap">Salary</th>
                <th className="p-3 whitespace-nowrap">Joining Date</th>
                <th className="p-3 text-center whitespace-nowrap">Status</th>
                <th className="p-3 text-center whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? (
                currentRows.map((row, idx) => (
                  <tr key={row.id ?? row.empId} className={`border-b hover:bg-gray-50 transition-colors ${idx % 2 !== 0 ? "bg-[#f7f6fe]" : ""}`}>
                    <td className="p-3 text-gray-700">{row.user_id}</td>
                    <td className="p-3 whitespace-nowrap text-gray-700">{row.payroll_date}</td>
                    <td className="p-3 whitespace-nowrap text-gray-800">{row.first_name} {row.last_name}</td>
                    <td className="p-3 text-gray-700">{row.bank}</td>
                    <td className="p-3 text-gray-700">{row.acc_no}</td>
                    <td className="p-3 text-gray-700">{row.deg_name}</td>
                    <td className="p-3 text-gray-700">{row.salary}</td>
                    <td className="p-3 whitespace-nowrap text-gray-700">{row.joiningdate}</td>
                    <td className="p-3 text-center"><span className={statusBadge(row.status)}>{row.status}</span></td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-3">
                        <button className="text-blue-600 hover:text-blue-800" title="Edit" onClick={() => handleEdit(row)}>
                          <Edit2 size={17} />
                        </button>
                        <button className="text-red-600 hover:text-red-800" title="Delete" onClick={() => { setSelectedRow(row); setDeleteModal(true); }}>
                          <Trash2 size={17} />
                        </button>
                        <button className="text-gray-600 hover:text-gray-800" title="View" onClick={() => { setSelectedRow(row); setViewModal(true); }}>
                          <Eye size={17} />
                        </button>
                        <button className="text-green-600 hover:text-green-800" title="Download Pay Slip PDF" onClick={() => downloadPayslipPDF(row)}>
                          <Download size={17} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="text-center py-10 text-gray-400">No records found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3 p-3">
          {currentRows.length > 0 ? (
            currentRows.map((row) => (
              <div key={row.id ?? row.empId} className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{row.first_name} {row.last_name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">ID: {row.user_id} · {row.deg_name}</p>
                  </div>
                  <span className={statusBadge(row.status)}>{row.status}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-gray-600 mb-3">
                  <div><span className="text-gray-400">Date</span><br />{row.payroll_date}</div>
                  <div><span className="text-gray-400">Salary</span><br />{row.salary}</div>
                  <div><span className="text-gray-400">Bank</span><br />{row.bank}</div>
                  <div><span className="text-gray-400">Account No</span><br />{row.acc_no}</div>
                </div>
                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  <button className="flex-1 flex items-center justify-center gap-1 text-blue-600 hover:bg-blue-50 py-1.5 rounded-lg text-xs font-medium" onClick={() => handleEdit(row)}>
                    <Edit2 size={14} /> Edit
                  </button>
                  <button className="flex-1 flex items-center justify-center gap-1 text-gray-600 hover:bg-gray-50 py-1.5 rounded-lg text-xs font-medium" onClick={() => { setSelectedRow(row); setViewModal(true); }}>
                    <Eye size={14} /> View
                  </button>
                  <button className="flex-1 flex items-center justify-center gap-1 text-green-600 hover:bg-green-50 py-1.5 rounded-lg text-xs font-medium" onClick={() => downloadPayslipPDF(row)}>
                    <Download size={14} /> PDF
                  </button>
                  <button className="flex-1 flex items-center justify-center gap-1 text-red-600 hover:bg-red-50 py-1.5 rounded-lg text-xs font-medium" onClick={() => { setSelectedRow(row); setDeleteModal(true); }}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-10 text-gray-400">No records found</div>
          )}
        </div>

        {/* Pagination — inside the same card as the table */}
        <PaginationBar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          totalPages={totalPages}
        />
      </div>

      {/* ════════════ GENERATE-ALL MODAL ════════════ */}
      {generateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
            {!genDone ? (
              <>
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={gradientStyle}>
                  <Zap size={30} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-1">Generate All Payslips</h3>
                <p className="text-sm text-gray-500 mb-6">
                  This will download a PDF payslip for <strong>{recordsForGenMonth.length}</strong> record{recordsForGenMonth.length === 1 ? "" : "s"} found for <strong>{genMonth}</strong>.
                </p>
                {generating ? (
                  <div className="space-y-3">
                    <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="h-2.5 rounded-full transition-all duration-200"
                        style={{ width: `${genTotal ? (genProgress / genTotal) * 100 : 0}%`, ...gradientStyle }}
                      />
                    </div>
                    <p className="text-sm text-gray-500">Downloading {genProgress} of {genTotal}…</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="bg-blue-50 rounded-xl p-3 text-left text-sm text-blue-800 mb-4 space-y-1">
                      <p>✓ {recordsForGenMonth.length} payslip record{recordsForGenMonth.length === 1 ? "" : "s"} for {genMonth}</p>
                      <p>✓ Each one downloads as its own PDF</p>
                      <p>✓ Your browser may prompt to allow multiple downloads — please allow it</p>
                    </div>
                    <div className="flex gap-3">
                      <button onClick={() => setGenerateModal(false)} className="flex-1 border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                      <button onClick={handleGenerateAll} className={`flex-1 ${gradientBtn} flex items-center justify-center gap-2`} style={gradientStyle}>
                        <Zap size={15} /> Start
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={36} className="text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-1">Payslips Downloaded!</h3>
                <p className="text-sm text-gray-500 mb-6">
                  <strong>{genTotal} payslip{genTotal === 1 ? "" : "s"}</strong> downloaded for {genMonth}.
                </p>
                <button onClick={() => setGenerateModal(false)} className={`w-full ${gradientBtn}`} style={gradientStyle}>Done</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ════════════ ADD MODAL ════════════ */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-start sm:items-center p-0 sm:p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-none sm:rounded-2xl shadow-lg p-5 sm:p-8 w-full sm:max-w-4xl relative min-h-screen sm:min-h-0 sm:max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">Add New PaySlip</h3>
              <button onClick={handleCloseAddModal} className="text-gray-500 hover:text-gray-800 p-1">
                <X size={22} />
              </button>
            </div>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
                  <select className={`border border-gray-300 rounded-lg p-2 w-full text-gray-700 text-sm ${focusRing}`} value={selectedEmployee} onChange={(e) => handleSelectEmployee(e.target.value)}>
                    <option value="">-- Select an Employee --</option>
                    {employees && employees.length > 0 ? (
                      employees.filter((emp) => emp.status === "Active").map((emp) => (
                        <option key={emp.empId} value={emp.empId}>{emp.first_name} {emp.last_name}</option>
                      ))
                    ) : (
                      <option disabled>No employees found</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    className={`border border-gray-300 rounded-lg p-2 w-full text-gray-700 text-sm ${focusRing}`}
                    value={selectedDate}
                    onChange={(e) => {
                      const date = e.target.value;
                      setSelectedDate(date);
                      if (selectedEmployee) {
                        fetchAttendanceSummary(selectedEmployee, date);
                      } else {
                        alert("Please select an employee first!");
                      }
                    }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: "Department", value: employeeDetails?.dep_name },
                  { label: "Designation", value: employeeDetails?.deg_name },
                  { label: "Bank Name", value: employeeDetails?.bank },
                  { label: "IFSC Code", value: employeeDetails?.ifsc },
                  { label: "Account Number", value: employeeDetails?.acc_no },
                  { label: "PAN Number", value: employeeDetails?.pan_no },
                  { label: "UAN Number", value: employeeDetails?.uan_no },
                  { label: "Total Payable Days", value: totalWorkingDays },
                  { label: "Days Payable", value: payableDays },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                    <input placeholder={label} className="border rounded-lg p-2 w-full text-sm bg-gray-50" value={value || ""} readOnly />
                  </div>
                ))}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Joining Date</label>
                  <input type="date" className="border rounded-lg p-2 w-full text-sm bg-gray-50" value={employeeDetails?.joiningdate || ""} readOnly />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4 pt-1">
                <span className="text-gray-700 font-medium text-sm">Status:</span>
                {["Paid", "Unpaid", "Generated"].map((s) => (
                  <label key={s} className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="status" value={s} checked={status === s} onChange={(e) => setStatus(e.target.value)} className="accent-[#2563eb]" />
                    <span className="text-sm">{s}</span>
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">HRA</label>
                  <input placeholder="HRA" value={hra} onChange={(e) => setHra(e.target.value)} className={`border rounded-lg p-2 w-full text-sm ${focusRing}`} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Special Allowance</label>
                  <input placeholder="Special Allowance" value={specialAllowance} onChange={(e) => setSpecialAllowance(e.target.value)} className={`border rounded-lg p-2 w-full text-sm ${focusRing}`} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">MED (Medical)</label>
                  <input placeholder="MED (Medical)" value={med} onChange={(e) => setMed(e.target.value)} className={`border rounded-lg p-2 w-full text-sm ${focusRing}`} />
                </div>
              </div>
              <div>
                <h4 className="text-gray-700 font-semibold text-sm mb-3">Contribution</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">PF Employee</label>
                    <input placeholder="PF Employee" value={pfEmployee} onChange={(e) => setPfEmployee(e.target.value)} className={`border rounded-lg p-2 w-full text-sm ${focusRing}`} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Taxes & Deductions</label>
                    <input placeholder="Taxes & Deductions" value={taxesDeductions} onChange={(e) => setTaxesDeductions(e.target.value)} className={`border rounded-lg p-2 w-full text-sm ${focusRing}`} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Professional Tax</label>
                    <input placeholder="Professional Tax" value={professionalTax} onChange={(e) => setProfessionalTax(e.target.value)} className={`border rounded-lg p-2 w-full text-sm ${focusRing}`} />
                  </div>
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4">
                <button type="button" onClick={handleCloseAddModal} className="border border-gray-300 px-5 py-2 rounded-lg hover:bg-gray-100 text-sm w-full sm:w-auto">Cancel</button>
                <button type="submit" className="text-white px-6 py-2 rounded-lg text-sm w-full sm:w-auto" style={gradientStyle}>Submit</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ════════════ EDIT MODAL ════════════ */}
      {editModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-start sm:items-center p-0 sm:p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-none sm:rounded-2xl shadow-lg p-5 sm:p-8 w-full sm:max-w-4xl relative min-h-screen sm:min-h-0 sm:max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-xl sm:text-2xl font-semibold text-gray-800">Edit Payslip</h3>
              <button onClick={() => setEditModal(false)} className="text-gray-500 hover:text-gray-800 p-1"><X size={22} /></button>
            </div>
            <form className="space-y-4" onSubmit={handleEditSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee Name</label>
                  <input type="text" value={`${selectedRow?.first_name} ${selectedRow?.last_name}`} readOnly className="border border-gray-300 rounded-lg p-2 w-full bg-gray-100 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payroll Date</label>
                  <input type="date" value={editSelectedDate} onChange={(e) => setEditSelectedDate(e.target.value)} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Payable Days</label>
                  <input type="number" value={editTotalWorkingDays} onChange={(e) => setEditTotalWorkingDays(e.target.value)} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Days Payable</label>
                  <input type="number" value={editPayableDays} onChange={(e) => setEditPayableDays(e.target.value)} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={editStatus} onChange={(e) => setEditStatus(e.target.value)} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`}>
                  <option value="Paid">Paid</option>
                  <option value="Generated">Generated</option>
                  <option value="Unpaid">Unpaid</option>
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">HRA</label>
                  <input type="number" value={editHra} onChange={(e) => setEditHra(e.target.value)} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Special Allowance</label>
                  <input type="number" value={editSpecialAllowance} onChange={(e) => setEditSpecialAllowance(e.target.value)} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">MED (Medical)</label>
                  <input type="number" value={editMed} onChange={(e) => setEditMed(e.target.value)} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PF Employee</label>
                  <input type="number" value={editPfEmployee} onChange={(e) => setEditPfEmployee(e.target.value)} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Taxes & Deductions</label>
                  <input type="number" value={editTaxesDeductions} onChange={(e) => setEditTaxesDeductions(e.target.value)} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Professional Tax</label>
                  <input type="number" value={editProfessionalTax} onChange={(e) => setEditProfessionalTax(e.target.value)} className={`border border-gray-300 rounded-lg p-2 w-full text-sm ${focusRing}`} />
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4">
                <button type="button" onClick={() => setEditModal(false)} className="border border-gray-300 px-5 py-2 rounded-lg hover:bg-gray-100 text-sm w-full sm:w-auto">Cancel</button>
                <button type="submit" className="text-white px-6 py-2 rounded-lg text-sm w-full sm:w-auto" style={gradientStyle}>Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ════════════ VIEW MODAL — earnings/deductions breakdown ════════════ */}
      {viewModal && selectedRow && (() => {
        const num = (v) => parseFloat(String(v ?? "0").replace(/[^\d.-]/g, "")) || 0;
        const baseSalary = num(selectedRow.basic_salary);
        const hraVal = num(selectedRow.hra);
        const specialVal = num(selectedRow.special_allowances);
        const medVal = num(selectedRow.med);
        const gross = baseSalary + hraVal + specialVal + medVal;
        const pfVal = num(selectedRow.pf_employee);
        const taxVal = num(selectedRow.taxes_deductions);
        const ptVal = num(selectedRow.professional_tax);
        const totalDed = pfVal + taxVal + ptVal;
        const netPay = gross - totalDed;

        return (
          <div className="fixed inset-0 bg-black/40 flex justify-center items-start sm:items-center p-0 sm:p-4 z-50 overflow-y-auto">
            <div className="bg-white rounded-none sm:rounded-2xl shadow-lg w-full sm:max-w-2xl relative min-h-screen sm:min-h-0 sm:max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">{selectedRow.first_name} {selectedRow.last_name}</h3>
                  <p className="text-sm text-gray-400">{selectedRow.deg_name} · {selectedRow.payroll_date}</p>
                </div>
                <button onClick={() => setViewModal(false)}><X size={20} className="text-gray-500" /></button>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  {[
                    ["Employee ID", selectedRow.user_id],
                    ["Bank", selectedRow.bank],
                    ["Account No", selectedRow.acc_no],
                    ["Days Payable", selectedRow.days_payable],
                    ["Status", selectedRow.status],
                    ["Joining Date", selectedRow.joiningdate],
                  ].map(([l, v]) => (
                    <div key={l} className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs text-gray-400">{l}</p>
                      <p className="font-semibold text-gray-800">{v || "—"}</p>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-green-50 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-green-800 mb-3">Earnings</h4>
                    {[["Basic+DA", baseSalary], ["HRA", hraVal], ["Special Allowance", specialVal], ["MED", medVal]].map(([l, v]) => (
                      <div key={l} className="flex justify-between text-sm py-1 border-b border-green-100 last:border-0">
                        <span className="text-gray-600">{l}</span>
                        <span className="font-medium text-gray-800">₹{v.toLocaleString()}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm py-2 font-bold text-green-700 mt-1">
                      <span>Gross</span><span>₹{gross.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="bg-red-50 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-red-800 mb-3">Deductions</h4>
                    {[["PF Employee", pfVal], ["Taxes & Deductions", taxVal], ["Professional Tax", ptVal]].map(([l, v]) => (
                      <div key={l} className="flex justify-between text-sm py-1 border-b border-red-100 last:border-0">
                        <span className="text-gray-600">{l}</span>
                        <span className="font-medium text-red-700"> ₹{v.toLocaleString()}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm py-2 font-bold text-red-700 mt-1">
                      <span>Total Deductions</span><span>₹{totalDed.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl p-4 text-white flex justify-between items-center" style={gradientStyle}>
                  <span className="text-lg font-bold">Net Pay</span>
                  <span className="text-2xl font-black">₹{netPay.toLocaleString()}</span>
                </div>
              </div>
              <div className="flex justify-end gap-3 p-6 border-t">
                <button onClick={() => setViewModal(false)} className="border px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Close</button>
                <button onClick={() => downloadPayslipPDF(selectedRow)} className={`${gradientBtn} flex items-center gap-2`} style={gradientStyle}>
                  <Download size={14} /> Download PDF
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ════════════ DELETE MODAL ════════════ */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-end sm:items-center p-0 sm:p-4 z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-lg p-6 sm:p-8 w-full sm:max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800">Delete Payslip</h3>
              <button onClick={() => setDeleteModal(false)} className="text-gray-500 hover:text-gray-800 p-1"><X size={22} /></button>
            </div>
            <p className="text-gray-600 mb-6 text-sm sm:text-base">
              Are you sure you want to delete the payslip for{" "}
              <span className="font-semibold text-gray-800">{selectedRow?.first_name} {selectedRow?.last_name}</span>? This action cannot be undone.
            </p>
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
              <button onClick={() => setDeleteModal(false)} className="border border-gray-300 px-5 py-2 rounded-lg hover:bg-gray-100 text-sm w-full sm:w-auto">Cancel</button>
              <button onClick={() => handleDelete(selectedRow?.id)} className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 text-sm w-full sm:w-auto">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ROOT — Tab shell
// ════════════════════════════════════════════════════════════════════════════
export default function PayrollModule() {
  const [tab, setTab] = useState("payslips");

  const tabs = [
    { id: "bonus",     label: "Bonus Management",   icon: <Gift size={16}/> },
    { id: "documents", label: "Payroll Documents",  icon: <FileText size={16}/> },
    { id: "payslips",  label: "Payslip Generation", icon: <Zap size={16}/> },
  ];

  return (
    <div className="bg-sky-50 min-h-screen">
      <div className="px-4 sm:px-6 lg:px-9 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Payroll</h1>
          <p className="text-sm text-gray-400 mt-0.5">Manage bonuses, documents, and generate payslips</p>
        </div>

        {/* Tab bar */}
        <div className="bg-white rounded-xl p-1 flex gap-1 mb-6 shadow-sm overflow-x-auto">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex-1 justify-center ${
                tab === t.id
                  ? "text-white shadow-sm"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
              style={tab === t.id ? gradientStyle : {}}
            >
              {t.icon}
              <span className="hidden sm:inline">{t.label}</span>
              <span className="sm:hidden">{t.label.split(" ")[0]}</span>
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === "bonus"     && <BonusManagement/>}
        {tab === "documents" && <PayrollDocuments/>}
        {tab === "payslips"  && <PayslipGeneration/>}
      </div>
    </div>
  );
}

// Named export so you can drop the real Payslip tab in on its own,
// without the Bonus/Documents tab shell, if that's all you need.
export { PayslipGeneration };